import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import Paper from "@mui/material/Paper";

const SystemUpdate = () => {
  const axiosPrivate = useAxiosPrivate();
  const [updateStatus, setUpdateStatus] = useState("checking"); // checking, available, up_to_date, error, installing
  const [latestVersion, setLatestVersion] = useState("");
  const [currentVersion, setCurrentVersion] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");
  const [lastUpdateStatus, setLastUpdateStatus] = useState(null);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [installMessage, setInstallMessage] = useState("");
  const [pollStatusMessage, setPollStatusMessage] = useState("");

  useEffect(() => {
    let isMounted = true;
    const checkUpdates = async () => {
      try {
        const response = await axiosPrivate.get('/api/system/check-updates');
        if (isMounted) {
          if (response.data.last_update_status) {
            setLastUpdateStatus(response.data.last_update_status);
          }
          if (response.data.update_available) {
            setUpdateStatus("available");
            setLatestVersion(response.data.latest_version);
            setCurrentVersion(response.data.current_version);
            setDownloadUrl(response.data.download_url);
          } else {
            setUpdateStatus("up_to_date");
            setCurrentVersion(response.data.current_version);
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error("Failed to check updates:", error);
          setUpdateStatus("error");
        }
      }
    };
    
    checkUpdates();
    return () => { isMounted = false; };
  }, [axiosPrivate]);

  const startPollingForCompletion = (targetVer) => {
    setIsPolling(true);
    setPollStatusMessage("Waiting for application services to restart...");

    const intervalId = setInterval(async () => {
      try {
        const res = await axiosPrivate.get('/api/system/check-updates');
        if (res && res.data) {
          const statusData = res.data.last_update_status;
          
          if (statusData && statusData.status === "failed") {
            clearInterval(intervalId);
            setIsPolling(false);
            setIsInstalling(false);
            setUpdateStatus("error");
            setLastUpdateStatus(statusData);
            setInstallMessage("Update failed. System was automatically rolled back.");
            return;
          }

          const curVer = res.data.current_version;
          const cleanCur = curVer ? curVer.replace(/^v/, '') : '';
          const cleanTarget = targetVer ? targetVer.replace(/^v/, '') : '';

          if (cleanCur && (cleanCur === cleanTarget || statusData?.status === "success")) {
            clearInterval(intervalId);
            setPollStatusMessage(`Update complete! Upgraded to ${curVer}. Reloading portal...`);
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          }
        }
      } catch (err) {
        // Backend service is offline during file swap & service restart
        setPollStatusMessage("Applying update & restarting background services... Please wait.");
      }
    }, 4000);
  };

  const handleInstallUpdate = async () => {
    setIsInstalling(true);
    setInstallMessage("Downloading update package from update server...");
    try {
      const response = await axiosPrivate.post('/api/system/install-update', {
        download_url: downloadUrl,
        target_version: latestVersion
      });
      setInstallMessage("Update downloaded! Applying installation files...");
      setUpdateStatus("installing");

      // Start continuous polling loop to detect service recovery and auto-reload
      startPollingForCompletion(latestVersion);
    } catch (error) {
      console.error("Failed to install update:", error);
      setInstallMessage(error.response?.data?.message || "Failed to initiate update. Please check the logs.");
      setIsInstalling(false);
    }
  };

  return (
    <Paper sx={{ p: 4, borderRadius: "6px", borderTopLeftRadius: 0, boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)" }}>
      <Typography variant="h6" gutterBottom>System Updates</Typography>
      
      {lastUpdateStatus && lastUpdateStatus.status === "failed" && !isInstalling && (
        <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
          <strong>Last Update Attempt Failed</strong> (Attempted: {lastUpdateStatus.attempted_version || 'Unknown'})
          <br />
          {lastUpdateStatus.error_message || 'An error occurred during update installation.'}
          {lastUpdateStatus.rolled_back && (
            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600, color: '#d32f2f' }}>
              ✓ System was automatically rolled back to your previous working version.
            </Typography>
          )}
        </Alert>
      )}

      {updateStatus === "checking" && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <CircularProgress size={20} />
          <Typography>Checking for updates...</Typography>
        </Box>
      )}

      {updateStatus === "error" && !isInstalling && (
        <Alert severity="warning" sx={{ mt: 2 }}>Could not check for system updates. Ensure the server has internet access.</Alert>
      )}

      {updateStatus === "up_to_date" && !isInstalling && (
        <Alert severity="success" sx={{ mt: 2 }}>Your system is up to date. (Current Version: {currentVersion || 'Unknown'})</Alert>
      )}

      {updateStatus === "available" && !isInstalling && (
        <Alert severity="info" sx={{ mt: 2 }} action={
          <Button color="primary" variant="contained" size="small" onClick={handleInstallUpdate} disabled={isInstalling}>
            Install Update
          </Button>
        }>
          A new update is available! <strong>{latestVersion}</strong> (Current: {currentVersion || 'Unknown'})
        </Alert>
      )}

      {(isInstalling || isPolling) && (
        <Box sx={{ mt: 3, p: 3, border: "1px solid #bbdefb", borderRadius: "6px", bgcolor: "#e3f2fd" }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <CircularProgress size={24} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Updating Brakepress System to {latestVersion}...
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {installMessage}
          </Typography>
          <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', color: '#1565c0', fontWeight: 500 }}>
            {pollStatusMessage || "Please keep this browser window open. The portal will automatically refresh when services come back online."}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default SystemUpdate;
